import React, { useState } from 'react';
import { Layout, Menu, Button, Drawer, Typography } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    HomeOutlined,
    TrophyOutlined,
    DollarOutlined,
    LogoutOutlined,
    MenuUnfoldOutlined,
    UserOutlined,
    LockOutlined,
    TeamOutlined
} from '@ant-design/icons';
import axiosClient from '../../api/axiosClient';
import logo from '../../sitelogo.png';
import { CUSTOMERNAME } from '../appsettings';

const { Sider } = Layout;
const { Text } = Typography;

const MemberSidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileVisible, setMobileVisible] = useState(false);

    const menuItems = [
        {
            key: '/dash',
            icon: <HomeOutlined />,
            label: 'My Profile',
            onClick: () => { navigate('/dash'); setMobileVisible(false); }
        },
        {
            key: '/metrics',
            icon: <TrophyOutlined />,
            label: 'My Progress',
            onClick: () => { navigate('/metrics'); setMobileVisible(false); }
        },
        {
            key: '/invoices',
            icon: <DollarOutlined />,
            label: 'Billing & Invoices',
            onClick: () => { navigate('/invoices'); setMobileVisible(false); }
        },
        {
            key: '/trainers',
            icon: <TeamOutlined />,
            label: 'My Trainers',
            onClick: () => { navigate('/trainers'); setMobileVisible(false); }
        },
        {
            key: '/change-password',
            icon: <LockOutlined />,
            label: 'Change Password',
            onClick: () => { navigate('/change-password'); setMobileVisible(false); }
        }
    ];

    const logout = async () => {
        const role = localStorage.getItem("USERROLE");
        const logoutUrl = role === "MEMBER" ? '/portal/logout.php' : '/user/logout.php';
        try {
            await axiosClient.post(logoutUrl);
        } catch (error) {
            console.error("Logout failed", error);
        } finally {
            localStorage.clear();
            navigate('/login');
        }
    };

    const SiderContent = (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff' }}>
            {/* Logo Section */}
            <div style={{ 
                padding: '24px 16px', 
                textAlign: 'center', 
                borderBottom: '1px solid #f0f0f0',
                marginBottom: 16
            }}>
                <img src={logo} alt="logo" style={{ height: 40, marginBottom: 8 }} />
                {!collapsed && (
                    <div style={{ 
                        color: '#1a1a1a', 
                        fontWeight: 800, 
                        fontSize: '1.1rem', 
                        lineHeight: '1.2',
                        marginTop: 4,
                        letterSpacing: '-0.5px',
                        wordWrap: 'break-word',
                        whiteSpace: 'normal',
                        padding: '0 4px'
                    }}>
                        {CUSTOMERNAME}
                    </div>
                )}
            </div>

            {/* User Info */}
            {!collapsed && (
                <div style={{ padding: '0 16px', textAlign: 'center', marginBottom: 24 }}>
                    <div style={{ 
                        width: 64, 
                        height: 64, 
                        borderRadius: '20px', 
                        background: '#f0fcfb', 
                        margin: '0 auto 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #b2e8e5',
                        boxShadow: '0 4px 12px rgba(32,156,150,0.1)'
                    }}>
                        <UserOutlined style={{ fontSize: 32, color: '#209c96' }} />
                    </div>
                    <Text strong style={{ color: '#1a1a1a', display: 'block', fontSize: '15px' }}>{localStorage.getItem("USERDPNAME")}</Text>
                    <Text type="secondary" style={{ color: '#8c8c8c', fontSize: '12px' }}>Personal Account</Text>
                </div>
            )}

            {/* Menu Section */}
            <Menu
                theme="light"
                mode="inline"
                selectedKeys={[location.pathname]}
                items={menuItems}
                className="member-sidebar-menu"
                style={{ border: 'none', flex: 1, padding: '0 8px' }}
            />

            {/* Logout Section */}
            <div style={{ padding: '16px', borderTop: '1px solid #f0f0f0' }}>
                <Button 
                    type="text" 
                    icon={<LogoutOutlined />} 
                    danger 
                    block={!collapsed}
                    onClick={logout}
                    style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        height: 48,
                        borderRadius: 12,
                        fontWeight: 500
                    }}
                >
                    {!collapsed && <span>Logout</span>}
                </Button>
            </div>
            
            <style>{`
                .member-sidebar-menu .ant-menu-item {
                    border-radius: 12px !important;
                    margin-bottom: 8px !important;
                    height: 48px !important;
                    line-height: 48px !important;
                }
                .member-sidebar-menu .ant-menu-item-selected {
                    background: #f0fcfb !important;
                    color: #209c96 !important;
                }
                .member-sidebar-menu .ant-menu-item-selected .anticon {
                    color: #209c96 !important;
                }
            `}</style>
        </div>
    );

    return (
        <>
            {/* Desktop Sider */}
            <Sider
                collapsible
                collapsed={collapsed}
                onCollapse={(val) => setCollapsed(val)}
                breakpoint="lg"
                collapsedWidth={80}
                onBreakpoint={(broken) => {
                    if (broken) setCollapsed(true);
                }}
                width={260}
                style={{
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    height: '100vh',
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    zIndex: 100,
                    boxShadow: '4px 0 24px rgba(0,0,0,0.05)',
                    background: '#fff'
                }}
                trigger={null}
            >
                {SiderContent}
            </Sider>

            {/* Mobile Header/Menu Toggle */}
            <div className="mobile-only" style={{ 
                position: 'fixed', 
                top: 0, 
                left: 0, 
                right: 0, 
                height: 64, 
                background: '#fff', 
                padding: '0 16px', 
                display: 'none',
                alignItems: 'center', 
                justifyContent: 'space-between',
                zIndex: 90,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                borderBottom: '1px solid #f0f0f0'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <img src={logo} alt="logo" style={{ height: 32 }} />
                    <span style={{ fontWeight: 700, color: '#1a1a1a' }}>{CUSTOMERNAME}</span>
                </div>
                <Button 
                    type="text" 
                    icon={<MenuUnfoldOutlined style={{ fontSize: 24, color: '#209c96' }} />} 
                    onClick={() => setMobileVisible(true)} 
                />
            </div>

            {/* Mobile Drawer */}
            <Drawer
                placement="left"
                onClose={() => setMobileVisible(false)}
                open={mobileVisible}
                bodyStyle={{ padding: 0 }}
                width={260}
                headerStyle={{ display: 'none' }}
                closeIcon={null}
            >
                {SiderContent}
            </Drawer>

            {/* CSS for responsive display */}
            <style>{`
                @media (max-width: 991px) {
                    .mobile-only { display: flex !important; }
                    .ant-layout-sider { display: none !important; }
                }
            `}</style>
        </>
    );
};

export default MemberSidebar;
